import {Component, computed, input, output} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {DateTime} from 'luxon';
import {map} from 'rxjs';

import {FacultyAppContextService} from 'libs/shared/faculty/src/lib/service/faculty-app-context.service';
import {InstitutionContextService} from 'libs/data-access/institution/src/lib/services/institution-context.service';
import {FacultyNotification} from 'libs/data-access/notification/src/lib/models/request-response-notification.models';
import {generateHumanizedDate} from 'libs/shared/util/src/lib/_helpers/date-helper';
import {PermissionCodesEnum} from 'libs/shared/permission/src/lib/enums/permissions.enum';
import {NotificationActionsService} from '../../../services/notification-actions.service';
import {CommonModule} from '@angular/common';

@Component({
    selector: 'blockgroup-window-closed-notifiaction',
    imports: [CommonModule],
    template: `
        <div
            id="outer-container"
            class="px-2 d-flex w-100 flex-column justify-content-center"
            [ngClass]="condensedView() ? 'mt-0' : 'mt-3'"
        >
            <!-- Card text -->
            <div [ngClass]="condensedView() ? 'mb-0' : 'mb-2'">
                <strong>{{ this.notification()?.BlockGroupWindowClosedNotification?.BlockGroupName }}</strong>
                window is closed for {{ this.learnerText()?.PluralForm }}.
                <br />
                {{ this.notification()?.BlockGroupWindowClosedNotification?.CompletedLearners }}/{{
                    this.notification()?.BlockGroupWindowClosedNotification?.TotalLearners
                }}
                have completed the
                {{ this.notification().BlockGroupWindowClosedNotification?.IsQuiz ? 'quiz' : 'assessment' }}
            </div>
            <div [ngClass]="condensedView() ? 'mb-1' : 'mb-3'">
                <small>{{ dateText() }}</small>
            </div>
            <div class="d-flex">
                <div [ngClass]="condensedView() ? 'mb-2' : 'mb-3'">
                    @if (viewButtonDisplayed()) {
                        <button
                            id="view-test-btn"
                            type="button"
                            class="btn btn-outline-secondary"
                            [ngClass]="condensedView() ? 'btn-sm' : 'btn-md'"
                            (click)="onViewBlockGroupClick()"
                            [disabled]="quizCheckInProgress()"
                        >
                            View {{ notification().BlockGroupWindowClosedNotification?.IsQuiz ? 'Quiz' : 'Assessment' }}
                        </button>
                    }

                    @if (viewReportDisplayed()) {
                        <button
                            id="view-report-btn"
                            type="button"
                            class="btn btn-outline-secondary ms-2"
                            [ngClass]="condensedView() ? 'btn-sm' : 'btn-md'"
                            (click)="onViewReportClick()"
                            [disabled]="viewReportDisabled()"
                        >
                            View Report
                        </button>
                    }
                </div>
            </div>
        </div>
    `
})
export class BlockgroupWindowClosedNotificationComponent {
    readonly notification = input.required<FacultyNotification>();
    readonly notificationRead = output<void>();
    condensedView = input(false);

    permissionsCodes = PermissionCodesEnum;

    learnerText = this.institutionServiceContext.contextLearnerText;

    quizCheckInProgress = this.notificationActionsService.quizCheckInProgress;

    dateText = computed<string>(() => generateHumanizedDate(this.notification()?.AvailableDate ?? DateTime.now()));

    permissions = toSignal(
        this.facultyAppContext.contextAccountInformation$.pipe(map((account) => account.Account.Permissions))
    );

    isInstitutionAdmin = toSignal(
        this.facultyAppContext.contextAccountInformation$.pipe(map((account) => account.Account.IsInstitutionAdmin))
    );

    viewReportDisabled = computed<boolean>(() => {
        const notification = this.notification();
        return (
            !notification.BlockGroupWindowClosedNotification?.BlockGroupId ||
            (!notification.BlockGroupWindowClosedNotification?.IsQuiz &&
                !notification.BlockGroupWindowClosedNotification?.AssessmentExamId) ||
            notification.BlockGroupWindowClosedNotification?.CompletedLearners < 1
        );
    });

    viewReportDisplayed = computed<boolean>(() => {
        if (this.isInstitutionAdmin()) {
            return true;
        }
        const notification = this.notification();
        const examPermissions = this.permissions().filter(
            (p) => +p.DataValue2 === notification.ExamYearId && +p.DataValue3 === notification.ExamBankId
        );

        if (notification.BlockGroupWindowClosedNotification?.IsQuiz) {
            const hasAccessExam = examPermissions.some((p) => p.Code === this.permissionsCodes.AccessExam);
            const hasViewQuizResults = examPermissions.some((p) => p.Code === this.permissionsCodes.ViewQuizResults);
            const hasManageAllQuizFolders = this.permissions().some(
                (p) => p.Code === this.permissionsCodes.ManageAllQuizFolders
            );

            return hasAccessExam && (hasManageAllQuizFolders || hasViewQuizResults);
        } else {
            const hasViewAssessments = this.permissions().some((p) => p.Code === this.permissionsCodes.ViewAssessments);
            const hasViewIndividualPerformance = this.permissions().some(
                (p) => p.Code === this.permissionsCodes.ViewIndividualPerformance
            );

            return hasViewAssessments && hasViewIndividualPerformance;
        }
    });

    viewButtonDisplayed = computed<boolean>(() => {
        if (this.isInstitutionAdmin()) {
            return true;
        }
        const notification = this.notification();
        if (notification.BlockGroupWindowClosedNotification?.IsQuiz) {
            return this.permissions().some(
                (p) =>
                    p.Code === this.permissionsCodes.AccessExam &&
                    +p.DataValue2 === notification.ExamYearId &&
                    +p.DataValue3 === notification.ExamBankId
            );
        } else {
            return this.permissions().some((p) => p.Code === this.permissionsCodes.ViewAssessments);
        }
    });

    constructor(
        private institutionServiceContext: InstitutionContextService,
        private facultyAppContext: FacultyAppContextService,
        private notificationActionsService: NotificationActionsService
    ) {}

    onViewBlockGroupClick(): void {
        const notification = this.notification();
        if (notification.BlockGroupWindowClosedNotification?.IsQuiz) {
            this.notificationActionsService.openQuiz(notification.BlockGroupWindowClosedNotification?.BlockGroupId);
        } else {
            window.open(
                // eslint-disable-next-line max-len
                `/assessments/view/${notification.BlockGroupWindowClosedNotification?.BlockGroupId}/${notification.ExamYearId}`,
                '_blank'
            );
        }

        this.notificationRead.emit();
    }

    onViewReportClick(): void {
        if (this.viewReportDisabled()) {
            return;
        }

        const notification = this.notification();
        if (notification.BlockGroupWindowClosedNotification?.IsQuiz) {
            window.open(
                `/(fullscreen:report-viewer/${notification.BlockGroupWindowClosedNotification?.BlockGroupId})`,
                '_blank'
            );
        } else {
            window.open(
                // eslint-disable-next-line max-len
                `/(fullscreen:report-viewer/${notification.BlockGroupWindowClosedNotification?.BlockGroupId}/${notification.BlockGroupWindowClosedNotification?.AssessmentExamId})`,
                '_blank'
            );
        }

        this.notificationRead.emit();
    }
}
